import type { OptionSource } from "@/shared/contracts/ports";

/**
 * De onde as opções de uma escolha vêm, quando não vêm escritas no campo (§3.11).
 *
 * `config.options` responde "pago / pendente / cancelado" e nada que mude sozinho.
 * `config.source` responde o resto: um artefato `query` do tenant, e as duas
 * colunas que viram valor e rótulo. Os dois convivem — um campo com `source` usa o
 * `source`, e a lista escrita fica onde está para quem abrir a configuração;
 * apagar uma pela outra é perder o que a pessoa escreveu.
 *
 * ── POR QUE NÃO DENTRO DE `field-config.ts` ───────────────────────────────
 *
 * Porque aquele arquivo já estava em 288 linhas e isto o levava a 331. O limite de
 * 300 não é decoração: um arquivo que passa dele está escondendo dois. Ler
 * `config.source` é uma leitura pura como as de lá, mas é a única que descreve um
 * trabalho ASSÍNCRONO a ser feito por uma porta — e é essa a costura que merece um
 * nome próprio.
 */
export function optionSourceOf(config: Record<string, unknown>): OptionSource | null {
	const raw = config.source;
	if (!raw || typeof raw !== "object") return null;

	const said = raw as { artifact?: unknown; valueField?: unknown; labelField?: unknown };
	if (typeof said.artifact !== "string" || said.artifact.length === 0) return null;
	if (typeof said.valueField !== "string" || said.valueField.length === 0) return null;

	// `labelField` cai no `valueField` de propósito: uma consulta que devolve uma
	// coluna só é o caso mais comum — os horários livres de um dia — e exigir as
	// duas seria pedir a mesma palavra duas vezes.
	const labelField = typeof said.labelField === "string" ? said.labelField : said.valueField;
	const inputs = inputsOf((raw as { inputs?: unknown }).inputs);
	return {
		artifact: said.artifact,
		valueField: said.valueField,
		labelField,
		...(inputs ? { inputs } : {}),
	};
}

/**
 * Os `inputs` que o campo fixa para o artefato, como texto.
 *
 * Texto porque é assim que eles viajam — querystring — e é o servidor que os
 * valida contra o `spec.inputs`. Um objeto aninhado é descartado em vez de virar
 * "[object Object]" numa URL.
 */
function inputsOf(raw: unknown): Record<string, string> | null {
	if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
	const out: Record<string, string> = {};
	for (const [name, value] of Object.entries(raw as Record<string, unknown>)) {
		if (value === null || value === undefined || typeof value === "object") continue;
		out[name] = String(value);
	}
	return Object.keys(out).length > 0 ? out : null;
}
